// src/pages/ForumPage.js
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Divider,
  Paper,
  TextField,
  Tab,
  Tabs,
  Badge,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Breadcrumbs,
  Link,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Forum as ForumIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Comment as CommentIcon,
  ThumbUp as ThumbUpIcon,
  ThumbUpOutlined as ThumbUpOutlinedIcon,
  CheckCircle as CheckCircleIcon,
  PushPin as PushPinIcon,
  Lock as LockIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Reply as ReplyIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AppContext';
import useForumStore from '../context/forumStore';

function ForumPage() {
  const navigate = useNavigate();
  const { courseId, topicId } = useParams();
  const { user } = useAuth();
  const {
    getCourseForums,
    getForumTopics,
    getTopicById,
    getTopicPosts,
    createTopic,
    createPost,
    toggleTopicLike,
    togglePostLike,
    hasUserLikedTopic,
    hasUserLikedPost,
    markPostAsAnswer,
    togglePinTopic,
    toggleLockTopic,
    incrementViewCount,
  } = useForumStore();

  const [selectedForum, setSelectedForum] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [newTopicDialog, setNewTopicDialog] = useState(false);
  const [newTopicTitle, setNewTopicTitle] = useState('');
  const [newTopicContent, setNewTopicContent] = useState('');
  const [replyContent, setReplyContent] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuTopic, setMenuTopic] = useState(null);

  const currentCourseId = courseId || user?.courses?.[0] || 'intro-programacion';
  const forums = getCourseForums(currentCourseId);
  const topics = selectedForum ? getForumTopics(selectedForum.id) : [];
  const currentTopic = selectedTopic ? getTopicById(selectedTopic) : null;
  const posts = selectedTopic ? getTopicPosts(selectedTopic) : [];

  const isInstructor = user?.role === 'instructor' || user?.role === 'admin';

  // Manejar selección de topic
  const handleSelectTopic = (topic) => {
    setSelectedTopic(topic.id);
    incrementViewCount(topic.id);
  };

  // Crear nuevo topic
  const handleCreateTopic = () => {
    if (!newTopicTitle.trim() || !newTopicContent.trim() || !selectedForum) return;
    
    createTopic({
      forumId: selectedForum.id,
      courseId: currentCourseId,
      title: newTopicTitle,
      content: newTopicContent,
    }, {
      id: user?.id,
      name: user?.name,
      avatar: user?.avatar,
    });

    setNewTopicDialog(false);
    setNewTopicTitle('');
    setNewTopicContent('');
  };

  // Responder en topic
  const handleReply = () => {
    if (!replyContent.trim() || !selectedTopic) return;
    
    createPost(selectedTopic, replyContent, {
      id: user?.id,
      name: user?.name,
      avatar: user?.avatar,
    });
    
    setReplyContent('');
  };

  // Formatear fecha
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return 'Hoy';
    if (days === 1) return 'Ayer';
    if (days < 7) return `Hace ${days} días`;
    return date.toLocaleDateString();
  };

  // Vista de lista de foros
  const renderForumsList = () => (
    <Grid container spacing={3}>
      {forums.map((forum) => {
        const forumTopics = getForumTopics(forum.id);
        return (
          <Grid item xs={12} md={4} key={forum.id}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 },
              }}
              onClick={() => setSelectedForum(forum)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <ForumIcon />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight={600}>
                      {forum.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {forum.description}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Chip 
                        icon={<CommentIcon />} 
                        label={`${forumTopics.length} temas`} 
                        size="small" 
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );

  // Vista de lista de topics
  const renderTopicsList = () => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => setSelectedForum(null)}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" fontWeight={600}>
              {selectedForum?.title}
            </Typography>
          </Box>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => setNewTopicDialog(true)}
          >
            Nuevo Tema
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {topics.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <ForumIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary">
              No hay temas en este foro aún.
            </Typography>
            <Button 
              variant="outlined" 
              startIcon={<AddIcon />}
              onClick={() => setNewTopicDialog(true)}
              sx={{ mt: 2 }}
            >
              Crear el primer tema
            </Button>
          </Box>
        ) : (
          <List>
            {topics.map((topic, index) => {
              const liked = hasUserLikedTopic(topic.id, user?.id);
              
              return (
                <React.Fragment key={topic.id}>
                  {index > 0 && <Divider />}
                  <ListItem
                    sx={{
                      py: 2,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                      borderLeft: topic.isPinned ? '3px solid' : 'none',
                      borderColor: 'primary.main',
                    }}
                    onClick={() => handleSelectTopic(topic)}
                  >
                    <ListItemAvatar>
                      <Avatar src={topic.authorAvatar}>
                        {topic.authorName?.[0]}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          {topic.isPinned && (
                            <Tooltip title="Fijado">
                              <PushPinIcon fontSize="small" color="primary" />
                            </Tooltip>
                          )}
                          {topic.isLocked && (
                            <Tooltip title="Cerrado">
                              <LockIcon fontSize="small" color="warning" />
                            </Tooltip>
                          )}
                          {topic.isResolved && (
                            <Tooltip title="Resuelto">
                              <CheckCircleIcon fontSize="small" color="success" />
                            </Tooltip>
                          )}
                          <Typography variant="subtitle1" fontWeight={600}>
                            {topic.title}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 500 }}>
                            {topic.content?.substring(0, 100)}...
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                            <Typography variant="caption" color="text.disabled">
                              Por {topic.authorName} • {formatDate(topic.createdAt)}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip 
                          icon={<VisibilityIcon />} 
                          label={topic.viewCount} 
                          size="small" 
                          variant="outlined"
                        />
                        <Chip 
                          icon={<CommentIcon />} 
                          label={topic.postsCount} 
                          size="small" 
                          variant="outlined"
                        />
                        <Chip 
                          icon={liked ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />} 
                          label={topic.likesCount} 
                          size="small" 
                          variant={liked ? "filled" : "outlined"}
                          color={liked ? "primary" : "default"}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleTopicLike(topic.id, user?.id);
                          }}
                        />
                      </Box>
                      {isInstructor && (
                        <IconButton 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMenuAnchor(e.currentTarget);
                            setMenuTopic(topic);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        )}
      </CardContent>
    </Card>
  );

  // Vista de topic individual
  const renderTopicDetail = () => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton onClick={() => setSelectedTopic(null)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" fontWeight={600} sx={{ flex: 1 }}>
            {currentTopic?.title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {currentTopic?.isPinned && <Chip icon={<PushPinIcon />} label="Fijado" size="small" />}
            {currentTopic?.isLocked && <Chip icon={<LockIcon />} label="Cerrado" size="small" color="warning" />}
            {currentTopic?.isResolved && <Chip icon={<CheckCircleIcon />} label="Resuelto" size="small" color="success" />}
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Post original */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Avatar src={currentTopic?.authorAvatar} sx={{ width: 48, height: 48 }}>
              {currentTopic?.authorName?.[0]}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {currentTopic?.authorName}
                </Typography>
                {currentTopic?.authorId?.includes('prof') && (
                  <Chip label="Instructor" size="small" color="primary" />
                )}
                <Typography variant="caption" color="text.secondary">
                  • {formatDate(currentTopic?.createdAt)}
                </Typography>
              </Box>
              <Typography 
                variant="body1" 
                sx={{ 
                  whiteSpace: 'pre-wrap',
                  '& code': { 
                    bgcolor: 'action.hover', 
                    px: 1, 
                    py: 0.5, 
                    borderRadius: 1,
                    fontFamily: 'monospace',
                  },
                }}
              >
                {currentTopic?.content}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Button
                  size="small"
                  startIcon={hasUserLikedTopic(currentTopic?.id, user?.id) ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
                  onClick={() => toggleTopicLike(currentTopic?.id, user?.id)}
                  color={hasUserLikedTopic(currentTopic?.id, user?.id) ? "primary" : "inherit"}
                >
                  {currentTopic?.likesCount} Me gusta
                </Button>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Respuestas */}
        <Typography variant="h6" gutterBottom>
          💬 Respuestas ({posts.length})
        </Typography>

        {posts.map((post) => {
          const liked = hasUserLikedPost(currentTopic?.id, post.id, user?.id);
          const isAnswer = post.isAnswer;
          
          return (
            <Paper 
              key={post.id} 
              variant="outlined" 
              sx={{ 
                p: 2, 
                mb: 2,
                borderColor: isAnswer ? 'success.main' : 'divider',
                borderWidth: isAnswer ? 2 : 1,
                bgcolor: isAnswer ? 'success.dark' : 'transparent',
              }}
            >
              {isAnswer && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  ✅ Marcado como respuesta correcta
                </Alert>
              )}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Avatar src={post.authorAvatar} sx={{ width: 40, height: 40 }}>
                  {post.authorName?.[0]}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {post.authorName}
                    </Typography>
                    {post.authorId?.includes('prof') && (
                      <Chip label="Instructor" size="small" color="primary" sx={{ height: 20 }} />
                    )}
                    <Typography variant="caption" color="text.secondary">
                      • {formatDate(post.createdAt)}
                    </Typography>
                  </Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      whiteSpace: 'pre-wrap',
                      '& code': { 
                        bgcolor: 'action.hover', 
                        px: 1, 
                        py: 0.5, 
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        display: 'block',
                        mt: 1,
                      },
                    }}
                  >
                    {post.content}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Button
                      size="small"
                      startIcon={liked ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
                      onClick={() => togglePostLike(currentTopic?.id, post.id, user?.id)}
                      color={liked ? "primary" : "inherit"}
                    >
                      {post.likesCount}
                    </Button>
                    {isInstructor && !isAnswer && !currentTopic?.isResolved && (
                      <Button
                        size="small"
                        startIcon={<CheckCircleIcon />}
                        color="success"
                        onClick={() => markPostAsAnswer(currentTopic?.id, post.id)}
                      >
                        Marcar como respuesta
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            </Paper>
          );
        })}

        {/* Formulario de respuesta */}
        {!currentTopic?.isLocked && (
          <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              ✍️ Tu respuesta
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Escribe tu respuesta..."
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<ReplyIcon />}
                onClick={handleReply}
                disabled={!replyContent.trim()}
              >
                Responder
              </Button>
            </Box>
          </Paper>
        )}

        {currentTopic?.isLocked && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Este tema está cerrado y no acepta nuevas respuestas.
          </Alert>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="lg">
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link 
            color="inherit" 
            href="#" 
            onClick={(e) => { 
              e.preventDefault(); 
              navigate(isInstructor ? '/instructor' : '/dashboard'); 
            }}
          >
            {isInstructor ? 'Dashboard Instructor' : 'Dashboard'}
          </Link>
          {selectedForum && (
            <Link 
              color="inherit" 
              href="#" 
              onClick={(e) => { e.preventDefault(); setSelectedForum(null); setSelectedTopic(null); }}
            >
              Foros
            </Link>
          )}
          {selectedTopic && (
            <Link 
              color="inherit" 
              href="#" 
              onClick={(e) => { e.preventDefault(); setSelectedTopic(null); }}
            >
              {selectedForum?.title}
            </Link>
          )}
          <Typography color="text.primary">
            {selectedTopic ? currentTopic?.title?.substring(0, 30) + '...' : 
             selectedForum ? selectedForum.title : 'Foros'}
          </Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton onClick={() => navigate(isInstructor ? '/instructor' : '/dashboard')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight={700}>
            🗣️ Foros de Discusión
          </Typography>
        </Box>

        {/* Contenido */}
        {selectedTopic ? renderTopicDetail() :
         selectedForum ? renderTopicsList() :
         renderForumsList()}

        {/* Dialog para nuevo tema */}
        <Dialog open={newTopicDialog} onClose={() => setNewTopicDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Crear Nuevo Tema</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Título del tema"
              placeholder="Escribe un título descriptivo..."
              value={newTopicTitle}
              onChange={(e) => setNewTopicTitle(e.target.value)}
              sx={{ mb: 2, mt: 1 }}
            />
            <TextField
              fullWidth
              multiline
              rows={6}
              label="Contenido"
              placeholder="Describe tu pregunta o tema de discusión..."
              value={newTopicContent}
              onChange={(e) => setNewTopicContent(e.target.value)}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Tip: Puedes usar ``` para incluir código
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNewTopicDialog(false)}>Cancelar</Button>
            <Button 
              variant="contained" 
              onClick={handleCreateTopic}
              disabled={!newTopicTitle.trim() || !newTopicContent.trim()}
            >
              Crear Tema
            </Button>
          </DialogActions>
        </Dialog>

        {/* Menú de acciones para instructor */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => { setMenuAnchor(null); setMenuTopic(null); }}
        >
          <MenuItem onClick={() => { togglePinTopic(menuTopic?.id); setMenuAnchor(null); }}>
            <PushPinIcon fontSize="small" sx={{ mr: 1 }} />
            {menuTopic?.isPinned ? 'Desfijar' : 'Fijar'}
          </MenuItem>
          <MenuItem onClick={() => { toggleLockTopic(menuTopic?.id); setMenuAnchor(null); }}>
            <LockIcon fontSize="small" sx={{ mr: 1 }} />
            {menuTopic?.isLocked ? 'Desbloquear' : 'Cerrar'}
          </MenuItem>
        </Menu>
      </Container>
    </Box>
  );
}

export default ForumPage;

